/**
 * Entry state machine for the segmented duration field (SegTime).
 *
 * Pure functions, no DOM — the React Aria behaviors (accumulate,
 * overflow-restart, predictive advance, unbounded minutes, hours
 * redistribution, delete-to-placeholder) live here so they can be unit
 * tested. See EDGE_CASES.md in this directory for the full catalog.
 */

export type SegKey = "hh" | "mm" | "ss";

export interface SegValues {
    hh: number | null;
    mm: number | null;
    ss: number | null;
}

export const segmentOrder = (hours: boolean): SegKey[] =>
    hours ? ["hh", "mm", "ss"] : ["mm", "ss"];

export const splitSeconds = (secs: number | null, hours: boolean): SegValues => {
    if (secs == null) {
        return {hh: null, mm: null, ss: null};
    }

    const h = hours ? Math.floor(secs / 3600) : 0;
    const rem = secs - h * 3600;

    return {
        hh: hours && h > 0 ? h : null,
        mm: Math.floor(rem / 60),
        ss: rem % 60,
    };
};

export const joinSeconds = (vals: SegValues): number | null => {
    if (vals.hh == null && vals.mm == null && vals.ss == null) {
        return null;
    }
    return (vals.hh ?? 0) * 3600 + (vals.mm ?? 0) * 60 + (vals.ss ?? 0);
};

// Seconds 0-59; hours 0-9 (single digit, cardio-scale); minutes 0-59 only once
// hours are in play, otherwise unbounded — a 90-minute run is "90:00".
export const maxFor = (key: SegKey, vals: SegValues, hours: boolean) =>
    key === "hh" ? 9 : key === "ss" ? 59 : hours && vals.hh != null ? 59 : null;

const maxLenFor = (key: SegKey) => (key === "hh" ? 1 : 2);

// Typing into hours while minutes hold >59 redistributes: mm=90 + "1" -> 2:30.
export const setSegment = (vals: SegValues, key: SegKey, value: number | null) => {
    let next = {...vals, [key]: value};

    if (key === "hh" && next.mm != null && next.mm > 59) {
        next = {
            ...next,
            hh: Math.min(9, (value ?? 0) + Math.floor(next.mm / 60)),
            mm: next.mm % 60,
        };
    }

    return next;
};

export interface InputResult {
    vals: SegValues;
    enteredKeys: string;
    advance: boolean;
}

export const handleInput = (
    vals: SegValues,
    key: SegKey,
    enteredKeys: string,
    data: string,
    hours: boolean,
): InputResult | null => {
    let newValue = enteredKeys + data;

    if (/^\d+$/.test(newValue) === false) {
        return null;
    }

    let num = parseInt(newValue, 10);
    const max = maxFor(key, vals, hours);

    if (max != null && num > max) {
        // Overflow restarts entry with the new key — typing never wedges.
        newValue = data.replace(/\D/g, "").slice(-1);
        num = parseInt(newValue, 10);

        if (isNaN(num) || num > max) {
            return null;
        }
    }

    // Advance when no further digit could fit, or max length reached.
    const advance =
        (max != null && Number(newValue + "0") > max) ||
        newValue.length >= maxLenFor(key);

    return {
        vals: setSegment(vals, key, num),
        enteredKeys: advance ? "" : newValue,
        advance,
    };
};

export interface BackspaceResult {
    vals: SegValues;
    enteredKeys: string;
    hop: boolean;
}

export const handleBackspace = (vals: SegValues, key: SegKey): BackspaceResult => {
    const current = vals[key];

    if (current == null) {
        // Empty segment: hop focus to the previous segment.
        return {vals, enteredKeys: "", hop: true};
    }

    const sliced = String(current).slice(0, -1);

    if (sliced === "" || parseInt(sliced, 10) === 0) {
        // Delete-to-zero clears to placeholder, never shows "0".
        return {vals: setSegment(vals, key, null), enteredKeys: "", hop: false};
    }

    return {
        vals: setSegment(vals, key, parseInt(sliced, 10)),
        enteredKeys: sliced,
        hop: false,
    };
};

export const step = (
    vals: SegValues,
    key: SegKey,
    delta: 1 | -1,
    hours: boolean,
): SegValues => {
    const max = maxFor(key, vals, hours) ?? 99;
    const next = Math.min(max, Math.max(0, (vals[key] ?? 0) + delta));
    return setSegment(vals, key, next);
};

// Field-chrome tap: first unfilled segment after the last filled one; minutes
// when fully empty (overrides React Aria's walk-back, which would land on hours).
export const smartFocusTarget = (vals: SegValues, hours: boolean): SegKey => {
    const order = segmentOrder(hours);
    let lastFilled = -1;

    order.forEach((key, index) => {
        if (vals[key] != null) {
            lastFilled = index;
        }
    });

    if (lastFilled === -1) {
        return "mm";
    }

    const next = order[Math.min(lastFilled + 1, order.length - 1)];
    return vals[next] == null ? next : order[order.length - 1];
};

export const displaySegment = (
    vals: SegValues,
    key: SegKey,
    hours: boolean,
): string | null => {
    const value = vals[key];

    if (value == null) {
        return null;
    }

    if (key === "ss" || (key === "mm" && hours && vals.hh != null)) {
        return String(value).padStart(2, "0");
    }

    return String(value);
};

export const formatSeconds = (secs: number | null): string => {
    if (secs == null || secs <= 0) {
        return "0:00";
    }

    const rounded = Math.round(secs);
    const h = Math.floor(rounded / 3600);
    const m = Math.floor((rounded % 3600) / 60);
    const s = rounded % 60;
    const mm = h > 0 ? String(m).padStart(2, "0") : String(m);

    return (h > 0 ? `${h}:${mm}:` : `${mm}:`) + String(s).padStart(2, "0");
};
